import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RequiredPatientFieldsFields from "@/components/patients/RequiredPatientFieldsFields";
import { PATIENTS } from "@/constants/testIds";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY_FORM = { name: "", phone: "", email: "", date_of_birth: "", emergency_contact_name: "", emergency_contact_phone: "", allergies: "" };

const REQUIRED_FIELD_TEST_IDS = {
  dob: PATIENTS.quickAddDobInput,
  emergencyName: PATIENTS.quickAddEmergencyNameInput,
  emergencyPhone: PATIENTS.quickAddEmergencyPhoneInput,
  allergies: PATIENTS.quickAddAllergiesInput,
};

/** Alta rápida de paciente desde el listado (nombre y teléfono siempre
 * obligatorios, más los campos que la clínica marcó como obligatorios). */
export default function PatientQuickAddModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [requiredFields, setRequiredFields] = useState([]);

  useEffect(() => {
    api.get("/clinic").then(({ data }) => setRequiredFields(data.required_patient_fields || [])).catch(() => {});
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (form.name.trim().length < 2) {
      setError("El nombre debe tener al menos 2 caracteres");
      return;
    }
    if (form.phone.trim().length < 6) {
      setError("El teléfono es obligatorio");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onCreated(form);
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setForm(EMPTY_FORM); setError(""); } }}>
      <DialogContent className="max-w-md" data-testid={PATIENTS.quickAddModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Nuevo paciente</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre completo *</Label>
            <Input value={form.name} onChange={set("name")} data-testid={PATIENTS.quickAddNameInput} placeholder="Ej: Camila Torres" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono *</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid={PATIENTS.quickAddPhoneInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Email{requiredFields.includes("email") ? " *" : ""}</Label>
            <Input type="email" value={form.email} onChange={set("email")} data-testid={PATIENTS.quickAddEmailInput} placeholder={requiredFields.includes("email") ? "" : "Opcional"} />
          </div>
          <RequiredPatientFieldsFields requiredFields={requiredFields} form={form} setForm={setForm} testIds={REQUIRED_FIELD_TEST_IDS} />
          {error && <p className="text-sm text-destructive" data-testid={PATIENTS.quickAddErrorMessage}>{error}</p>}
          <p className="text-xs text-muted-foreground">Vas a poder completar la historia clínica, etiquetas y mas desde la ficha del paciente.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={PATIENTS.quickAddCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={PATIENTS.quickAddSaveButton}>
            {saving ? "Creando..." : "Crear paciente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
